import { createSlice } from '@reduxjs/toolkit';

import { fetchSpendByCategory } from '../services/fetchSpendByCategory';
import { fetchSpendBySupplier } from '../services/fetchSpendBySupplier';
import { fetchSpendFilterSuppliers } from '../services/fetchSpendFilterSuppliers';
import { fetchSpendOvertime } from '../services/fetchSpendOvertime';
import { fetchSpendProducts } from '../services/fetchSpendProducts';
import { fetchSpendSummary } from '../services/fetchSpendSummary';

import type {
    ReportSpendSummarySchema,
    SpendInsightsLastRequestParams,
} from '../types/ReportSpendSummarySchema';

const initialState: ReportSpendSummarySchema = {
    summary: null,
    summaryLoading: false,
    summaryError: null,
    products: null,
    productsLoading: false,
    productsError: null,
    byCategory: [],
    byCategoryLoading: false,
    byCategoryError: null,
    bySupplier: [],
    bySupplierLoading: false,
    bySupplierError: null,
    overtime: [],
    overtimeLoading: false,
    overtimeError: null,
    filterSuppliers: [],
    filterSuppliersLoading: false,
    filterSuppliersError: null,
    lastRequestParams: null,
};

export const reportSpendSummarySlice = createSlice({
    name: 'reportSpendSummary',
    initialState,
    reducers: {
        setSpendInsightsLastRequestParams: (
            state,
            action: { payload: SpendInsightsLastRequestParams },
        ) => {
            state.lastRequestParams = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSpendSummary.pending, (state) => {
                state.summaryLoading = true;
                state.summaryError = null;
            })
            .addCase(fetchSpendSummary.fulfilled, (state, action) => {
                state.summary = action.payload;
                state.summaryLoading = false;
                state.summaryError = null;
            })
            .addCase(fetchSpendSummary.rejected, (state, action) => {
                state.summaryLoading = false;
                state.summaryError = action.payload ?? 'error';
            })
            .addCase(fetchSpendProducts.pending, (state) => {
                state.productsLoading = true;
                state.productsError = null;
            })
            .addCase(fetchSpendProducts.fulfilled, (state, action) => {
                state.products = action.payload;
                state.productsLoading = false;
                state.productsError = null;
            })
            .addCase(fetchSpendProducts.rejected, (state, action) => {
                state.productsLoading = false;
                state.productsError = action.payload ?? 'error';
            })
            .addCase(fetchSpendByCategory.pending, (state) => {
                state.byCategoryLoading = true;
                state.byCategoryError = null;
            })
            .addCase(fetchSpendByCategory.fulfilled, (state, action) => {
                state.byCategory = action.payload;
                state.byCategoryLoading = false;
                state.byCategoryError = null;
            })
            .addCase(fetchSpendByCategory.rejected, (state, action) => {
                state.byCategoryLoading = false;
                state.byCategoryError = action.payload ?? 'error';
            })
            .addCase(fetchSpendBySupplier.pending, (state) => {
                state.bySupplierLoading = true;
                state.bySupplierError = null;
            })
            .addCase(fetchSpendBySupplier.fulfilled, (state, action) => {
                state.bySupplier = action.payload;
                state.bySupplierLoading = false;
                state.bySupplierError = null;
            })
            .addCase(fetchSpendBySupplier.rejected, (state, action) => {
                state.bySupplierLoading = false;
                state.bySupplierError = action.payload ?? 'error';
            })
            .addCase(fetchSpendOvertime.pending, (state) => {
                state.overtimeLoading = true;
                state.overtimeError = null;
            })
            .addCase(fetchSpendOvertime.fulfilled, (state, action) => {
                state.overtime = action.payload;
                state.overtimeLoading = false;
                state.overtimeError = null;
            })
            .addCase(fetchSpendOvertime.rejected, (state, action) => {
                state.overtimeLoading = false;
                state.overtimeError = action.payload ?? 'error';
            })
            .addCase(fetchSpendFilterSuppliers.pending, (state) => {
                state.filterSuppliersLoading = true;
                state.filterSuppliersError = null;
            })
            .addCase(fetchSpendFilterSuppliers.fulfilled, (state, action) => {
                state.filterSuppliers = action.payload;
                state.filterSuppliersLoading = false;
                state.filterSuppliersError = null;
            })
            .addCase(fetchSpendFilterSuppliers.rejected, (state, action) => {
                state.filterSuppliersLoading = false;
                state.filterSuppliersError = action.payload ?? 'error';
            });
    },
});

export const { reducer: reportSpendSummaryReducer } = reportSpendSummarySlice;

export const { setSpendInsightsLastRequestParams } =
    reportSpendSummarySlice.actions;
